// Printable trip / loading / unloading sheets: the same content as the PDFs, built from the sheet data and shown in a new window
// with the browser's print dialog (as the collections (PDC) print).
import { COLLECTION_TYPES, escapeHtml, fieldsFor, fmtDate, fmtDateTime, money, sumBy } from "./common";

const e = escapeHtml;
const qty = (value) => (value === null || value === undefined ? "-" : Number(value).toLocaleString("en-IN", { maximumFractionDigits: 3 }));
const qtyOrDash = (value) => (!value || Number(value) === 0 ? "-" : qty(value));
const dayName = (iso) => (iso ? new Date(`${String(iso).slice(0, 10)}T00:00:00`).toLocaleDateString("en-IN", { weekday: "long" }) : "");

/** The fields a distributor can print on a sheet, with the default heading of each (settings page). */
export const SHEET_FIELDS = {
  trip: [
    ["srNo", "Sr No"],
    ["salesmanName", "Salesman"],
    ["beatName", "Beat"],
    ["retailerCode", "Retailer ID"],
    ["customerName", "Customer Name"],
    ["invoiceNumber", "Invoice No"],
    ["invoiceDate", "Invoice Date"],
    ["grossValue", "Gross Value"],
    ["schemeAmount", "Scheme Amt"],
    ["stockistDiscount", "Stockist Disc"],
    ["taxAmount", "Tax Amt"],
    ["netAmount", "Net Amt"],
    ["creditNoteAmount", "NPS Disc / Credit Note"],
    ["returnAmount", "Return / Cancel Value"],
    ["netPayable", "Net Payable"],
  ],
  loading: [
    ["srNo", "Sr No"],
    ["productCode", "Product Code"],
    ["productName", "Product Description"],
    ["category", "Category"],
    ["uom", "UOM"],
    ["mrp", "MRP"],
    ["alternateUom", "Alt UOM"],
    ["alternateUomSize", "Alt UOM Size"],
    ["salesCases", "CS"],
    ["salesDozens", "DZ"],
    ["salesUnits", "EA/IP"],
    ["totalQuantity", "Total Qty (EA/IP)"],
    ["boxes", "Boxes"],
    ["nos", "Nos"],
  ],
};

const TRIP_AMOUNTS = new Set(["grossValue", "schemeAmount", "stockistDiscount", "taxAmount", "netAmount", "creditNoteAmount", "returnAmount", "netPayable"]);
const LOADING_COUNTS = new Set(["salesCases", "salesDozens", "salesUnits", "totalQuantity", "boxes", "nos"]);

const fieldCell = (kind, key, row, index) => {
  if (key === "srNo") return `<td class="c">${index + 1}</td>`;
  if (kind === "trip" && TRIP_AMOUNTS.has(key)) return `<td class="r">${money(row[key])}</td>`;
  if (kind === "loading" && LOADING_COUNTS.has(key)) return `<td class="r">${qtyOrDash(row[key])}</td>`;
  if (key === "invoiceDate") return `<td class="c">${fmtDate(row[key])}</td>`;
  if (key === "mrp") return `<td class="r">${row.mrp === null || row.mrp === undefined ? "-" : money(row.mrp)}</td>`;
  if (key === "alternateUomSize") return `<td class="r">${qtyOrDash(row[key])}</td>`;
  return `<td>${e(row[key] || "")}</td>`;
};

/** The columns of the distributor's own format that the sheet knows; none: the default format. */
const printableColumns = (kind, layout) => {
  const known = new Set(SHEET_FIELDS[kind].map(([key]) => key));
  const columns = (layout?.columns || []).filter((column) => known.has(column.key));
  return columns.some((column) => column.key !== "srNo") ? columns : [];
};

/** A table of the columns configured for the sheet, with a total under the figures (as on the PDF). */
const configuredTable = (kind, columns, rows, none) => {
  const head = columns.map((column) => `<th>${e(column.heading || column.key)}</th>`).join("");
  if (!rows.length) return `<table><thead><tr>${head}</tr></thead><tbody><tr><td colspan="${columns.length}" class="c">${e(none)}</td></tr></tbody></table>`;
  const figures = kind === "trip" ? TRIP_AMOUNTS : LOADING_COUNTS;
  const body = rows.map((row, index) => `<tr>${columns.map((column) => fieldCell(kind, column.key, row, index)).join("")}</tr>`).join("");
  const firstTotal = columns.findIndex((column) => figures.has(column.key));
  const showTotal = (key) => {
    const total = sumBy(rows, (row) => row[key]);
    return kind === "trip" ? money(total) : qty(total);
  };
  const totals =
    firstTotal < 0
      ? ""
      : `<tr class="b">${firstTotal ? `<td colspan="${firstTotal}" class="r">Total (${rows.length})</td>` : ""}${columns
          .slice(firstTotal)
          .map((column) => (figures.has(column.key) ? `<td class="r">${showTotal(column.key)}</td>` : "<td></td>"))
          .join("")}</tr>`;
  return `<table><thead><tr>${head}</tr></thead><tbody>${body}${totals}</tbody></table>`;
};

const SIGNATURES = `<div class="sign">Loaded By: ____________________ &nbsp;&nbsp;&nbsp;&nbsp; Checked By: ____________________ &nbsp;&nbsp;&nbsp;&nbsp; Delivery Boy: ____________________</div>`;

const STYLE = (orientation) => `
  @page { size: A4 ${orientation}; margin: 8mm; }
  body { font-family: Helvetica, Arial, sans-serif; margin: 0; padding: 0 10px; color: #000; font-size: 10px; }
  .top { text-align: center; margin-bottom: 8px; }
  .top .company { font-size: 14px; font-weight: bold; }
  .top .title { font-size: 12px; font-weight: bold; margin-top: 2px; }
  .info { display: flex; justify-content: space-between; margin: 10px 0 6px; font-size: 11px; }
  .info .right { text-align: right; }
  h3 { font-size: 11px; margin: 10px 0 6px; }
  h4 { font-size: 10px; margin: 8px 0 4px; }
  table { width: 100%; border-collapse: collapse; margin-bottom: 8px; }
  th, td { border: 1px solid #000; font-size: 9px; padding: 3px 4px; vertical-align: top; }
  th { background: #e6e6e6; }
  thead { display: table-header-group; }
  tr { page-break-inside: avoid; }
  tr.group td { background: #eef2fa; font-weight: bold; }
  tr.b td, tr.sum td { font-weight: bold; }
  td.items { padding-left: 18px; }
  .c { text-align: center; } .r { text-align: right; white-space: nowrap; }
  .side { display: flex; gap: 16px; align-items: flex-start; page-break-inside: avoid; margin-top: 14px; }
  .side > div { flex: 1; }
  .side h3 { margin: 0 0 10px; }
  .total-line { font-weight: bold; font-size: 11px; margin: 4px 0; }
  .muted { color: #444; }
  .sign { margin-top: 24px; font-size: 10px; }
  table.bills th, table.bills td { font-size: 7.5px; padding: 2px 2px; overflow-wrap: anywhere; }
  table.bills td.r { overflow-wrap: normal; }
`;

/** Time now as hh:mm:ss AM / PM. */
const timeNow = () => new Date().toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true });

/** {@code withTime}: the time of printing after the date (loading sheet). */
const page = (title, sheet, body, withTime = false, orientation = "portrait") => `<!doctype html><html><head><meta charset="utf-8"><title>${e(title)}</title><style>${STYLE(orientation)}</style></head><body>
  <div class="top"><div class="company">${e(sheet.distributorName || "")}</div>
    <div class="title">${e(title)} - as on ${fmtDate(sheet.asOn)}${withTime ? ` ${e(timeNow())}` : ""} (${e(dayName(sheet.asOn))})</div></div>
  ${body}
</body></html>`;

const header = (loadSheetNumber, deliveryUser, salesman, beat, loadingDate) => `<div class="info">
  <div>Load Sheet No : ${e(loadSheetNumber || "")}<br/>Delivery Boy : ${e(deliveryUser || "")}<br/>Salesman : ${e(salesman || "")}</div>
  <div class="right">BEAT : ${e(beat || "")}${loadingDate ? `<br/>Loading Date : ${fmtDate(loadingDate)}` : ""}</div>
</div>`;

const VALUE_COLUMNS = [
  ["Gross Value", "grossValue"],
  ["Scheme Amt", "schemeAmount"],
  ["Stockist Disc", "stockistDiscount"],
  ["Tax Amt", "taxAmount"],
  ["Net Amt", "netAmount"],
  ["NPS Disc / Credit Note", "creditNoteAmount"],
  ["Return / Cancel Value", "returnAmount"],
  ["Net Payable", "netPayable"],
];

/** Bill values table, as on the PDF; {@code withBeat} adds the Beat column (trip sheet). */
const billValuesTable = (bills, withBeat) => {
  const leading = ["Sr No", "Salesman", ...(withBeat ? ["Beat"] : []), "Retailer ID", "Customer Name", "Invoice No"];
  const head = [...leading, ...VALUE_COLUMNS.map(([label]) => label)].map((h) => `<th>${e(h)}</th>`).join("");
  const columns = leading.length + VALUE_COLUMNS.length;
  if (!bills.length) return `<table class="bills"><thead><tr>${head}</tr></thead><tbody><tr><td colspan="${columns}" class="c">No bills</td></tr></tbody></table>`;
  const body = bills
    .map(
      (bill, index) => `<tr><td class="c">${index + 1}</td><td>${e(bill.salesmanName || "")}</td>${withBeat ? `<td>${e(bill.beatName || "-")}</td>` : ""}
        <td class="c">${e(bill.retailerCode || "")}</td><td>${e(bill.customerName || "")}</td><td class="c">${e(bill.invoiceNumber)}</td>
        ${VALUE_COLUMNS.map(([, key]) => `<td class="r">${money(bill[key])}</td>`).join("")}</tr>`
    )
    .join("");
  const totals = `<tr class="b"><td colspan="${leading.length}" class="r">Total (${bills.length} bills)</td>
    ${VALUE_COLUMNS.map(([, key]) => `<td class="r">${money(sumBy(bills, (b) => b[key]))}</td>`).join("")}</tr>`;
  return `<table class="bills"><thead><tr>${head}</tr></thead><tbody>${body}${totals}</tbody></table>`;
};

export const tripSheetHtml = (sheet) => {
  const groups = sheet.groups || [];
  const columns = printableColumns("trip", sheet.layout);
  const billsTable = (bills) => (columns.length ? configuredTable("trip", columns, bills, "No bills") : billValuesTable(bills, true));
  const blocks = groups.length
    ? groups
        .map(
          (group) => `<div>${header(group.loadSheetNumber, sheet.deliveryUserName, group.salesmanName, group.beatName, sheet.loadingDate)}
          ${(group.newBillValues || []).length ? `<h3>NEW BILLS</h3>${billsTable(group.newBillValues)}` : ""}
          ${(group.pendingBillValues || []).length ? `<h3>OLD PENDING BILLS</h3>${billsTable(group.pendingBillValues)}` : ""}
        </div>`
        )
        .join("")
    : `${header("N/A", sheet.deliveryUserName, "N/A", "N/A", sheet.loadingDate)}<p>No bills are assigned for the selected load sheet / dates.</p>`;
  return page(
    sheet.layout?.title?.trim() || "Trip Sheet",
    sheet,
    `${blocks}
    <div class="total-line" style="margin-top:10px">Total Amount Of New Bills: ${money(sheet.totalNewBills)}</div>
    <div class="total-line">Total Amount Of Pending Bills: ${money(sheet.totalPendingBills)}</div>`
  );
};

export const loadingSheetHtml = (sheet) => {
  const rows = sheet.rows || [];
  const title = sheet.layout?.title?.trim() || "Loading Sheet";
  const info = `<div class="info"><div>Load Sheet No : ${e(sheet.loadSheetNumber || "")}<br/>Delivery Boy : ${e(sheet.deliveryUserName || "")}</div>
      <div class="right">Loading Date : ${sheet.loadingDate ? fmtDate(sheet.loadingDate) : "N/A"}<br/>Bills : ${sheet.bills ?? 0}</div></div>`;
  const columns = printableColumns("loading", sheet.layout);
  if (columns.length) return page(title, sheet, `${info}${configuredTable("loading", columns, rows, "No products on the selected load sheet.")}${SIGNATURES}`, true);
  const body = rows.length
    ? rows
        .map(
          (row, index) => `<tr><td class="c">${index + 1}</td><td class="c">${e(row.productCode || "")}</td><td>${e(row.productName || "")}</td>
          <td class="r">${row.mrp === null || row.mrp === undefined ? "-" : money(row.mrp)}</td><td class="c">${e(row.alternateUom || "-")}</td>
          <td class="r">${qtyOrDash(row.alternateUomSize)}</td>
          <td class="r">${qtyOrDash(row.salesCases)}</td><td class="r">${qtyOrDash(row.salesDozens)}</td><td class="r">${qtyOrDash(row.salesUnits)}</td>
          <td class="r">${qty(row.totalQuantity)}</td></tr>`
        )
        .join("")
    : `<tr><td colspan="10" class="c">No products on the selected load sheet.</td></tr>`;
  return page(
    title,
    sheet,
    `${info}
    <table><thead>
      <tr><th rowspan="2">Sr No</th><th rowspan="2">Product Code</th><th rowspan="2">Product Description</th><th rowspan="2">MRP</th>
        <th rowspan="2">Alt UOM</th><th rowspan="2">Alt UOM Size</th><th colspan="3">Sales Qty</th>
        <th rowspan="2">Total Qty (EA/IP)</th></tr>
      <tr><th>CS</th><th>DZ</th><th>EA/IP</th></tr>
    </thead><tbody>${body}
      <tr class="b"><td colspan="9" class="r">Total (EA/IP)</td><td class="r">${qty(sheet.totalQuantity)}</td></tr>
    </tbody></table>
    ${SIGNATURES}`,
    true
  );
};

const itemsTable = (items) => `<table><thead><tr><th>Product</th><th>SKU Code</th><th>Qty</th><th>UOM</th><th>Rate</th><th>Disc</th><th>Tax</th><th>Amount</th></tr></thead>
  <tbody>${items
    .map(
      (item) => `<tr><td>${e(item.productName || "")}</td><td class="c">${e(item.skuCode || "")}</td><td class="r">${qty(item.quantity)}</td>
        <td class="c">${e(item.uom || "")}</td><td class="r">${money(item.unitPrice)}</td><td class="r">${money(item.discountAmount)}</td>
        <td class="r">${money(item.taxAmount)}${item.taxPercent ? ` (${qty(item.taxPercent)}%)` : ""}</td><td class="r">${money(item.lineTotal)}</td></tr>`
    )
    .join("")}</tbody></table>`;

const returnedTable = (products) => {
  const rows = products || [];
  const body = rows.length
    ? rows
        .map(
          (p, index) => `<tr><td class="c">${index + 1}</td><td class="c">${e(p.productCode || "")}</td><td>${e(p.productName || "")}</td>
          <td class="r">${qty(p.quantity)}</td><td class="c">${e(p.uom || "")}</td><td class="r">${money(p.unitPrice)}</td>
          <td class="r">${money(p.amount)}</td><td class="c">${p.bills}</td></tr>`
        )
        .join("") +
      `<tr class="b"><td colspan="3" class="r">Total</td><td class="r">${qty(sumBy(rows, (p) => p.quantity))}</td><td colspan="2"></td>
        <td class="r">${money(sumBy(rows, (p) => p.amount))}</td><td></td></tr>`
    : `<tr><td colspan="8" class="c">Nil</td></tr>`;
  return `<table><thead><tr><th>Sl</th><th>Product Code</th><th>Product</th><th>Qty</th><th>UOM</th><th>Rate</th><th>Value</th><th>Bills</th></tr></thead>
    <tbody>${body}</tbody></table>`;
};

const collectionStatus = (row) => {
  if (row.bounced) return "Bounced";
  if (row.approved) return "Approved";
  if (row.approvalRequired) return "Pending";
  return fieldsFor(row.collectionType).amount ? "Posted" : "Visit";
};

const daysSinceInvoice = (row) => {
  if (!row.invoiceDate || !row.collectedAt) return "-";
  const collected = new Date(row.collectedAt);
  const collectedDay = Date.UTC(collected.getFullYear(), collected.getMonth(), collected.getDate());
  const invoiceDay = Date.parse(`${String(row.invoiceDate).slice(0, 10)}T00:00:00Z`);
  return Math.max(0, Math.round((collectedDay - invoiceDay) / 86400000));
};

/** The delivery boy's collections by type, then the collection summary (with the bill counts) and the denomination summary. */
const collectionsHtml = (data, counts) => {
  const countRows = counts
    ? `<tr class="b"><td>Total No of Bills</td><td class="c">${counts.totalBills}</td><td></td></tr>
       <tr><td>Bills Collected</td><td class="c">${counts.billsCollected}</td><td></td></tr>
       <tr class="b"><td>Bills in Hand</td><td class="c">${counts.billsInHand}</td><td></td></tr>`
    : "";
  if (!data) {
    return `<p>Collections are not available.</p>${
      counts ? `<div class="side"><div><h3>Collection Summary</h3><table><tbody>${countRows}</tbody></table></div></div>` : ""
    }`;
  }
  const collections = data.collections || [];
  const groups = COLLECTION_TYPES.map((t) => ({ ...t, rows: collections.filter((c) => c.collectionType === t.value) })).filter((g) => g.rows.length);
  const COLS = 14;
  const body = groups.length
    ? groups
        .map(
          (group) => `<tr class="group"><td colspan="${COLS}">${e(group.label)} (${group.rows.length})</td></tr>${group.rows
            .map(
              (row) => `<tr><td>${e(row.collectedByName || "-")}</td><td>${e(row.customerName || "")}</td><td>${e(row.beatName || "-")}</td>
                <td>${e(row.invoiceNumber || "")}</td><td class="c">${fmtDate(row.invoiceDate)}</td><td class="c">${e(fmtDateTime(row.collectedAt))}</td>
                <td class="c">${daysSinceInvoice(row)}</td><td class="r">${money(row.openingBalance)}</td><td class="r">${money(row.amount)}</td>
                <td class="r">${money(row.closingBalance)}</td><td>${e(row.referenceNumber || "-")}</td><td>${e(row.bankName || "-")}</td>
                <td class="c">${fmtDate(row.chequeDate)}</td><td class="c">${collectionStatus(row)}</td></tr>`
            )
            .join("")}
            <tr class="sum"><td colspan="8">${e(group.label)} total</td><td class="r">${money(sumBy(group.rows, (r) => r.amount))}</td><td colspan="5"></td></tr>`
        )
        .join("")
    : `<tr><td colspan="${COLS}" class="c">No collections in this range</td></tr>`;
  const totals = (data.totals || [])
    .map((row) => {
      const strong = ["OPENING BALANCE", "PAID GRAND TOTAL", "CLOSING BALANCE"].includes(row.label);
      return `<tr class="${strong ? "b" : ""}"><td>${e(row.label)}</td><td class="c">${row.count ?? ""}</td><td class="r">${money(row.amount)}</td></tr>`;
    })
    .join("");
  const denominations = data.denominations
    ? `<div><h3>Denomination Summary</h3><table><thead><tr><th>Note</th><th>Count</th><th>Total</th></tr></thead><tbody>
        ${data.denominations.rows
          .map((row) => `<tr><td>${e(row.note)}</td><td class="c">${row.note === "Coins" ? "" : row.count}</td><td class="r">${money(row.total)}</td></tr>`)
          .join("")}
        <tr class="b"><td colspan="2">Grand Total</td><td class="r">${money(data.denominations.grandTotal)}</td></tr></tbody></table></div>`
    : "";
  return `<table class="bills"><thead><tr><th>Salesperson</th><th>Customer</th><th>Beat</th><th>Invoice</th><th>Inv. Date</th><th>Collected</th><th>Days</th>
      <th>Opening</th><th>Amount</th><th>Closing</th><th>Ref No</th><th>Bank</th><th>Chq/Txn Date</th><th>Status</th></tr></thead>
    <tbody>${body}</tbody></table>
    <div class="side">
      <div><h3>Collection Summary</h3><table><thead><tr><th>Collection Type</th><th>Payments</th><th>Amount</th></tr></thead>
        <tbody>${totals}${countRows}</tbody></table></div>
      ${denominations}
    </div>`;
};

export const unloadingSheetHtml = (sheet) => {
  const groups = sheet.groups || [];
  const blocks = groups.length
    ? groups
        .map(
          (group) => `<div>${header(group.loadSheetNumber, sheet.deliveryUserName, group.salesmanName, group.beatName, sheet.loadingDate)}
          <h3>RETURNED BILLS</h3>
          <table><thead><tr><th>Bill No</th><th>Name</th><th>Bill Date</th><th>Bill Amt</th><th>Credit Note No</th><th>Return Amt</th><th>Phone No</th></tr></thead>
          <tbody>${(group.bills || [])
            .map(
              (bill) => `<tr><td class="c">${e(bill.invoice.invoiceNumber)}</td><td>${e(bill.invoice.customerName || "")}</td>
                <td class="c">${fmtDate(bill.invoice.invoiceDate)}</td><td class="r">${money(bill.invoice.invoiceAmount)}</td>
                <td class="c">${e(bill.creditNoteNumbers || "")}</td><td class="r">${money(bill.returnedAmount)}</td><td class="c">${e(
                bill.invoice.customerPhone || ""
              )}</td></tr>
                ${(bill.items || []).length ? `<tr><td colspan="7" class="items">${itemsTable(bill.items)}</td></tr>` : ""}`
            )
            .join("")}</tbody></table>
          ${
            // per load sheet product summary: replaced by the one return summary by warehouse
            false && (group.products || []).length
              ? `<h3>RETURNED PRODUCTS SUMMARY (from the returned bills)</h3>
                <table><thead><tr><th>Sl</th><th>Product Code</th><th>Product</th><th>Qty</th><th>UOM</th><th>Bills</th></tr></thead><tbody>${group.products
                  .map(
                    (p, index) => `<tr><td class="c">${index + 1}</td><td class="c">${e(p.productCode || "")}</td><td>${e(p.productName || "")}</td>
                      <td class="r">${qty(p.quantity)}</td><td class="c">${e(p.uom || "")}</td><td class="c">${p.bills}</td></tr>`
                  )
                  .join("")}</tbody></table>
                <p>Products: ${group.products.length} &nbsp;&nbsp; Total quantity: ${qty(sumBy(group.products, (p) => p.quantity))}</p>`
              : ""
          }</div>`
        )
        .join("")
    : `${header("N/A", sheet.deliveryUserName, "N/A", "N/A", sheet.loadingDate)}<p>No goods were returned on the bills of the selected load sheet / dates.</p>`;
  const warehouses = (sheet.warehouses || []).length
    ? `<h3>RETURN SUMMARY OF ITEMS (NEW AND OLD BILLS) BY WAREHOUSE</h3>${sheet.warehouses
        .map((w) => {
          const type = !w.warehouseType ? "" : w.warehouseType === "GOOD" ? " (Good warehouse)" : " (Bad warehouse)";
          return `<h4>${e(w.warehouseName)}${type} — Qty: ${qty(w.quantity)} &nbsp; Value: ${money(w.amount)}</h4>${returnedTable(w.products)}`;
        })
        .join("")}`
    : `<h3>RETURN SUMMARY OF ITEMS (NEW AND OLD BILLS) BY WAREHOUSE</h3><p>No goods were returned.</p>`;
  const range = sheet.collectionFrom === sheet.collectionTo ? fmtDate(sheet.collectionFrom) : `${fmtDate(sheet.collectionFrom)} to ${fmtDate(sheet.collectionTo)}`;
  return page(
    "Unloading Sheet",
    sheet,
    `${blocks}
    <div class="total-line">Total Amount Of Returned Goods: ${money(sheet.totalReturned)}</div>
    ${warehouses}
    <h3>COLLECTIONS BY ${e(String(sheet.deliveryUserName || "").toUpperCase())} (${e(range)})</h3>
    ${collectionsHtml(sheet.collections, sheet.billCounts)}`
  );
};

export const SHEET_HTML = { trip: tripSheetHtml, loading: loadingSheetHtml, unloading: unloadingSheetHtml };

/**
 * Opens the print window right away (so the browser does not block it as a pop-up), loads the sheet into it and opens the print
 * dialog. {@code load} resolves to the sheet data.
 */
export const printSheet = async (kind, load) => {
  const win = window.open("", "_blank");
  if (!win) throw new Error("Allow pop-ups for this site to print the sheet");
  win.document.write("<p style='font-family:Arial;padding:16px'>Preparing the sheet...</p>");
  try {
    const sheet = await load();
    win.document.open();
    win.document.write(SHEET_HTML[kind](sheet));
    win.document.close();
    win.focus();
    setTimeout(() => win.print(), 300);
  } catch (error) {
    win.close();
    throw error;
  }
};
